import React, { useState, useMemo } from 'react';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  Search,
  Download,
  CreditCard,
  Banknote,
  Receipt,
  Building,
  Plane,
  Home,
  Utensils,
  Car,
  FileText,
  ArrowUpRight,
  ArrowDownLeft
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { mockCashBoxTransactions } from '../../data/mockData';
import { formatDate } from '../../utils/date';

const CashBoxReport = ({ dateRange }) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Use cash box transactions directly
  const transactions = useMemo(() => {
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    return mockCashBoxTransactions.map(transaction => ({
      id: transaction.id,
      date: transaction.date,
      type: transaction.transactionType === 'Payment In' ? 'income' : 'expense',
      category: transaction.relatedTo?.includes('Payment') ? 'payment' : 
                transaction.relatedTo?.includes('Accommodation') ? 'accommodation' :
                transaction.relatedTo?.includes('Commission') ? 'commission' : 'other',
      amount: transaction.amount,
      description: transaction.relatedTo || transaction.notes || 'Transaction',
      reference: transaction.notes || t('common.unknown'),
      paymentMethod: transaction.paymentMethod?.toLowerCase() || 'cash',
      status: 'completed'
    })).filter(transaction => {
      const transactionDate = new Date(transaction.date);
      return transactionDate >= startDate && transactionDate <= endDate;
    }).sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [dateRange, t]);

  // Filter and sort transactions
  const filteredTransactions = useMemo(() => {
    let filtered = transactions.filter(transaction => {
      const matchesSearch = transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           transaction.reference.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesType = transactionTypeFilter === 'all' || transaction.type === transactionTypeFilter;
      const matchesCategory = categoryFilter === 'all' || transaction.category === categoryFilter;
      
      return matchesSearch && matchesType && matchesCategory;
    });

    // Sort transactions
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'date':
          comparison = new Date(a.date) - new Date(b.date);
          break;
        case 'amount':
          comparison = a.amount - b.amount;
          break;
        case 'type':
          comparison = a.type.localeCompare(b.type);
          break;
        case 'category':
          comparison = a.category.localeCompare(b.category);
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'desc' ? -comparison : comparison;
    });

    return filtered;
  }, [transactions, searchTerm, transactionTypeFilter, categoryFilter, sortBy, sortOrder]);

  // Calculate statistics
  const statistics = useMemo(() => {
    const totalIncome = transactions
      .filter(t => t.type === 'income')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const totalExpenses = transactions
      .filter(t => t.type === 'expense')
      .reduce((sum, t) => sum + t.amount, 0);
    
    const netCashFlow = totalIncome - totalExpenses;
    
    // Category breakdown
    const incomeByCategory = {};
    const expensesByCategory = {};
    
    transactions.forEach(transaction => {
      if (transaction.type === 'income') {
        incomeByCategory[transaction.category] = (incomeByCategory[transaction.category] || 0) + transaction.amount;
      } else {
        expensesByCategory[transaction.category] = (expensesByCategory[transaction.category] || 0) + transaction.amount;
      }
    });
    
    // Payment method breakdown
    const cashTransactions = transactions.filter(t => t.paymentMethod === 'cash');
    const bankTransactions = transactions.filter(t => t.paymentMethod === 'bank');
    
    const cashBalance = cashTransactions.reduce((sum, t) => {
      return sum + (t.type === 'income' ? t.amount : -t.amount);
    }, 0);
    
    const bankBalance = bankTransactions.reduce((sum, t) => {
      return sum + (t.type === 'income' ? t.amount : -t.amount);
    }, 0);
    
    return {
      totalIncome,
      totalExpenses,
      netCashFlow,
      incomeByCategory,
      expensesByCategory,
      cashBalance,
      bankBalance,
      totalTransactions: transactions.length
    };
  }, [transactions]);

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'accommodation':
        return <Home className="w-4 h-4" />;
      case 'meals':
        return <Utensils className="w-4 h-4" />;
      case 'transport':
        return <Car className="w-4 h-4" />;
      case 'tickets':
        return <Plane className="w-4 h-4" />;
      case 'visa':
        return <FileText className="w-4 h-4" />;
      case 'payment':
        return <CreditCard className="w-4 h-4" />;
      case 'office':
        return <Building className="w-4 h-4" />;
      case 'marketing':
        return <TrendingUp className="w-4 h-4" />;
      case 'refund':
        return <Receipt className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const StatCard = ({ title, value, icon, color, subtitle }) => {
    const Icon = icon;
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-2">
              {title}
            </p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white leading-tight">
              {Math.abs(value).toLocaleString()} {t('common:currency')}
            </p>
            {subtitle && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                {subtitle}
              </p>
            )}
          </div>
          <div className={`p-3 rounded-lg ${color} flex-shrink-0 ml-4`}>
            <Icon className="w-6 h-6" />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <DollarSign className="w-8 h-8 text-green-600" />
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('reports.cashBoxReport')}
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              {t('reports.dateRangeDisplay', { 
                start: formatDate(dateRange.startDate),
                end: formatDate(dateRange.endDate)
              })}
            </p>
          </div>
        </div>
        <button className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors">
          <Download className="w-4 h-4" />
          {t('common.export')}
        </button>
      </div>

      {/* Financial Overview */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('reports.financialOverview')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title={t('cashbox.totalIncome')}
            value={statistics.totalIncome}
            icon={TrendingUp}
            color="bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400"
          />
          <StatCard
            title={t('cashbox.totalExpenses')}
            value={statistics.totalExpenses}
            icon={TrendingDown}
            color="bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400"
          />
          <StatCard
            title={t('cashbox.netCashFlow')}
            value={statistics.netCashFlow}
            icon={statistics.netCashFlow >= 0 ? TrendingUp : TrendingDown}
            color={statistics.netCashFlow >= 0 
              ? "bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400"
              : "bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400"
            }
          />
          <StatCard
            title={t('cashbox.totalTransactions')}
            value={statistics.totalTransactions}
            icon={Receipt}
            color="bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
          />
        </div>
      </div>

      {/* Balance Breakdown */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('cashbox.balanceBreakdown')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <StatCard
            title={t('cashbox.cashBalance')}
            value={statistics.cashBalance}
            icon={Banknote}
            color="bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400"
          />
          <StatCard
            title={t('cashbox.bankBalance')}
            value={statistics.bankBalance}
            icon={Building}
            color="bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('common.search')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>
          </div>
          <div className="flex flex-wrap gap-4">
            <select
              value={transactionTypeFilter}
              onChange={(e) => setTransactionTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">{t('cashbox.allTypes')}</option>
              <option value="income">{t('cashbox.income')}</option>
              <option value="expense">{t('cashbox.expense')}</option>
            </select>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="all">{t('common.allCategories')}</option>
              <option value="payment">{t('cashbox.payment')}</option>
              <option value="accommodation">{t('expenses.categories.accommodation')}</option>
              <option value="meals">{t('expenses.categories.meals')}</option>
              <option value="transport">{t('expenses.categories.transport')}</option>
              <option value="tickets">{t('expenses.categories.tickets')}</option>
              <option value="visa">{t('expenses.categories.visa')}</option>
              <option value="office">{t('cashbox.office')}</option>
              <option value="marketing">{t('cashbox.marketing')}</option>
              <option value="refund">{t('cashbox.refund')}</option>
            </select>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="date">{t('common.sortByDate')}</option>
              <option value="amount">{t('common.sortByAmount')}</option>
              <option value="type">{t('common.sortByType')}</option>
              <option value="category">{t('common.sortByCategory')}</option>
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
        </div>
      </div>

      {/* Transactions List */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {t('cashbox.transactionHistory')} ({filteredTransactions.length})
        </h3>
        <div>
          <div className="app-table-wrap">
            <table className="app-table w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('common.date')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('common.description')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('common.category')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('cashbox.paymentMethod')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('common.amount')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900 dark:text-white">
                          {formatDate(transaction.date)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {transaction.description}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {transaction.reference}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {getCategoryIcon(transaction.category)}
                        <span className="text-sm text-gray-900 dark:text-white">
                          {t(`cashbox.categories.${transaction.category}`, transaction.category)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        {transaction.paymentMethod === 'cash' ? (
                          <Banknote className="w-4 h-4 text-yellow-600" />
                        ) : (
                          <Building className="w-4 h-4 text-purple-600" />
                        )}
                        <span className="text-sm text-gray-900 dark:text-white">
                          {t(`cashbox.${transaction.paymentMethod}`)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-2">
                        {transaction.type === 'income' ? (
                          <ArrowUpRight className="w-4 h-4 text-green-600" />
                        ) : (
                          <ArrowDownLeft className="w-4 h-4 text-red-600" />
                        )}
                        <span className={`text-sm font-medium ${
                          transaction.type === 'income' 
                            ? 'text-green-600 dark:text-green-400' 
                            : 'text-red-600 dark:text-red-400'
                        }`}>
                          {transaction.type === 'income' ? '+' : '-'}{transaction.amount.toLocaleString()} {t('common:currency')}
                        </span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {filteredTransactions.length === 0 && (
            <div className="text-center py-12">
              <Receipt className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
                {t('common.noResults')}
              </h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                {t('cashbox.noTransactionsFound')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CashBoxReport;